import { useMemo, useState } from 'react';

import { Box, Paper, Stack, Popover, Tooltip, TextField, Typography, IconButton } from '@mui/material';
import { EmojiEmotionsOutlined as EmojiIcon } from '@mui/icons-material';

import { searchEmoji, EMOJI_CATEGORIES, type EmojiEntry } from './emoji-data';

// System emoji stack so the picker grid itself renders color glyphs in-app,
// regardless of the app's UI font.
const EMOJI_FONT_STACK =
  '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif';

const GRID_COLUMNS = 8;

function EmojiButton({ entry, onPick }: { entry: EmojiEntry; onPick: (char: string) => void }) {
  return (
    <Tooltip title={entry.name} disableInteractive enterDelay={400}>
      <Box
        component="button"
        type="button"
        aria-label={entry.name}
        // preventDefault on mousedown keeps focus (and the caret) in whatever
        // editable opened the picker — the parent TextField, the canvas overlay,
        // or the chat iframe's contentEditable — so the insert lands at the caret
        // instead of at the end. Same trick the format toolbars use.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onPick(entry.char)}
        sx={{
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: 1,
          bgcolor: 'transparent',
          cursor: 'pointer',
          fontFamily: EMOJI_FONT_STACK,
          fontSize: 20,
          lineHeight: 1,
          p: 0,
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {entry.char}
      </Box>
    </Tooltip>
  );
}

/**
 * The picker panel: a search box over a scrollable, category-grouped grid.
 * Context-agnostic — it only reports the picked character via `onPick`; the call
 * site owns how that character is inserted (execCommand on a contentEditable,
 * a caret splice on an input, etc.). `keepOpen` is the default so a user can drop
 * several emoji in a row.
 */
export function EmojiPicker({ onPick }: { onPick: (char: string) => void }) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => (query.trim() ? searchEmoji(query) : null), [query]);

  return (
    <Box sx={{ width: 320, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1, pb: 0.5 }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          value={query}
          placeholder="Search emoji…"
          onChange={(e) => setQuery(e.target.value)}
          // Keep the picker's own search box from being caught by the
          // focus-retention preventDefault of the grid buttons.
          inputProps={{ 'aria-label': 'Search emoji' }}
        />
      </Box>

      <Box sx={{ maxHeight: 280, overflowY: 'auto', px: 1, pb: 1 }}>
        {results ? (
          results.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, py: 1.5 }}>
              No emoji matched "{query.trim()}".
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
                gap: 0.25,
                pt: 0.5,
              }}
            >
              {results.map((entry) => (
                <EmojiButton key={entry.char} entry={entry} onPick={onPick} />
              ))}
            </Box>
          )
        ) : (
          EMOJI_CATEGORIES.map((category) => (
            <Box key={category.id} sx={{ pt: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'sticky',
                  top: 0,
                  display: 'block',
                  bgcolor: 'background.paper',
                  fontWeight: 600,
                  px: 0.5,
                  py: 0.5,
                  zIndex: 1,
                }}
              >
                {category.label}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
                  gap: 0.25,
                }}
              >
                {category.emoji.map((entry) => (
                  <EmojiButton key={`${category.id}-${entry.char}`} entry={entry} onPick={onPick} />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export interface EmojiPickerButtonProps {
  /** Called with the picked emoji character. Fires once per click; the panel
   *  stays open so several can be inserted in a row. */
  onPick: (char: string) => void;
  title?: string;
  disabled?: boolean;
  /** IconButton size; matches the surrounding toolbar. */
  size?: 'small' | 'medium';
}

/**
 * A drop-in "insert emoji" IconButton that opens the {@link EmojiPicker} in a
 * Popover. The Popover deliberately does NOT steal focus (disableAutoFocus /
 * disableEnforceFocus / disableRestoreFocus) so the editable that owns the caret
 * keeps it while the user browses — the picked emoji then inserts at the caret.
 */
export function EmojiPickerButton({ onPick, title = 'Insert emoji', disabled, size = 'small' }: EmojiPickerButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={title}>
        <span>
          <IconButton
            size={size}
            disabled={disabled}
            aria-label={title}
            // Preserve the caret in the underlying editable when opening.
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
          >
            <EmojiIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        <Paper elevation={0}>
          <EmojiPicker onPick={onPick} />
        </Paper>
      </Popover>
    </>
  );
}
